import { type FC, type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useCallback } from 'react';

import { randomUUID } from '@core/utils/uuid';

import { ToastContainer } from './ToastContainer';
import {
  type Toast,
  ToastAlertPosition,
  ToastContext,
  type ToastContextProps,
  type ToastProviderConfig,
  type ToastWithoutID,
} from './ToastContext';
import { ToastEmitter, ToastEventType } from './ToastEmitter';
import { createToastMessage } from './utils/createToastMessage';

interface ToastProviderProps extends PropsWithChildren {
  config?: ToastProviderConfig;
}

const createToast = (newToast: ToastWithoutID): Toast => {
  return {
    id: randomUUID(),
    ...newToast,
    message: newToast.message ?? createToastMessage(newToast),
  };
};

const createEmptyToastState = (): Record<ToastAlertPosition, Toast[]> => {
  return Object.keys(ToastAlertPosition).reduce(
    (acc, position) => ({ ...acc, [position]: [] }),
    {} as Record<ToastAlertPosition, Toast[]>,
  );
};

const defaultConfig: ToastProviderConfig = {
  position: ToastAlertPosition.topRight,
  autoDismissDuration: 3000,
  maxVisibleToasts: 4,
};

export const ToastProvider: FC<ToastProviderProps> = ({ children, config = defaultConfig }) => {
  const [toasts, setToasts] =
    useState<Record<ToastAlertPosition, Toast[]>>(createEmptyToastState());

  const removeAlert: ToastContextProps['removeAlert'] = useCallback((id) => {
    setToasts((prevToasts) => {
      return Object.entries(prevToasts).reduce((acc, [position, toastItems]) => {
        return { ...acc, [position]: toastItems.filter((t) => t.id !== id) };
      }, createEmptyToastState());
    });
  }, []);

  const showAlert: ToastContextProps['showAlert'] = useCallback(
    (newToast: ToastWithoutID) => {
      setToasts((prevToasts) => {
        const position = newToast.position ?? config.position;
        const maxVisibleToasts = newToast.config?.maxVisibleToasts ?? config.maxVisibleToasts;

        return {
          ...prevToasts,
          [position]: [createToast(newToast), ...prevToasts[position]].slice(0, maxVisibleToasts),
        };
      });
    },
    [config.maxVisibleToasts, config.position],
  );

  const toastContextValue = useMemo<ToastContextProps>(() => ({ removeAlert, showAlert }), []);

  useEffect(() => {
    ToastEmitter.on(ToastEventType.SHOW_TOAST, showAlert);

    return (): void => {
      ToastEmitter.off(ToastEventType.SHOW_TOAST, showAlert);

      setToasts(createEmptyToastState());
    };
  }, []);

  return (
    <ToastContext.Provider value={toastContextValue}>
      {(Object.keys(ToastAlertPosition) as ToastAlertPosition[]).map((position) => (
        <ToastContainer
          key={position}
          data={toasts[position]}
          removeAlert={removeAlert}
          config={config}
          position={position}
        />
      ))}

      {children}
    </ToastContext.Provider>
  );
};
